import { EmitterSubscription } from 'react-native';
import DefaultPreference from 'react-native-default-preference';
import {
    consentCategories,
    ConsentWatcher,
    IOneTrustMobile,
    OneTrustConfigMobile,
    OneTrustConsent,
} from '../types';
import { consentDisabled, consentNotReady } from '../consent';

export class OneTrustMobile implements IOneTrustMobile {
    protected sdk?: typeof import('react-native-onetrust-cmp').default;

    protected disabled = false;

    async init(config?: OneTrustConfigMobile) {
        if (!config?.enabled || !config.storageLocation || !config.appId) {
            this.disabled = true;
            return;
        }

        const sdk = (await import('react-native-onetrust-cmp'))?.default;
        if (!sdk) return;

        await sdk.startSDK(
            config.storageLocation,
            config.appId,
            config?.languageCode || 'en',
            {},
            false
        );
        this.sdk = sdk;
        this.setupSdkConsentListeners();
        this.dispatchConsentChange();
    }

    showPreferenceModal() {
        this.sdk?.showPreferenceCenterUI({});
    }

    async checkAndShowBanner() {
        const { sdk } = this;
        if (!sdk) return;
        if (await sdk.shouldShowBanner()) {
            sdk.showBannerUI({});
        }
    }

    private static extractConsent(javascriptCode: string): {
        tcString: string | undefined;
        gppString: string | undefined;
        gppSid: string | undefined;
    } | null {
        try {
            if (typeof javascriptCode !== 'string') return null;
            if (!javascriptCode.startsWith('var OTExternalConsent = {')) return null;
            return JSON.parse(javascriptCode.substring(24));
        } catch {
            return null;
        }
    }

    async getConsentStatus(): Promise<OneTrustConsent> {
        const { sdk } = this;
        if (!sdk) {
            return this.disabled ? consentDisabled : consentNotReady;
        }

        const categoryPromises = consentCategories.map((categoryId) =>
            sdk.getConsentStatusForCategory(categoryId)
        );
        const categoryResults = await Promise.all(categoryPromises);
        const personalAds = categoryResults.some((result) => result === 1);

        const domainInfo = await sdk.getDomainInfo();
        const webviewJs = await sdk.getOTConsentJSForWebView();

        // extract gppSid and gppString from default Preference
        const gppSid = await DefaultPreference?.get('IABGPP_GppSID');
        const gppString = await DefaultPreference?.get('IABGPP_HDR_GppString');
        const { tcString: tcfConsentString = null } =
            OneTrustMobile.extractConsent(webviewJs) || {};
        /* eslint-disable @typescript-eslint/dot-notation */
        const gppConsentString = domainInfo['countryCode'] === 'US' ? gppString : '';
        const gppSectionId = domainInfo['countryCode'] === 'US' ? gppSid : '';

        return {
            doNotTrack: !personalAds,
            limitAdvertisementTracking: !personalAds,
            tcfConsentString: tcfConsentString || null,
            gppConsentString: gppConsentString || '',
            gppSectionId: gppSectionId || '',
        };
    }

    private readonly watchers: ConsentWatcher[] = [];

    private consentEmitterSubscriptions: EmitterSubscription[] = [];

    dispatchConsentChange(): void {
        this.watchers.forEach((watcher) => watcher());
    }

    private setupSdkConsentListeners(): void {
        const { sdk } = this;
        if (!sdk) return;
        if (this.consentEmitterSubscriptions.length === 0 && this.watchers.length > 0) {
            sdk.setBroadcastAllowedValues(consentCategories);
            const sdkCallback = () => this.dispatchConsentChange();
            this.consentEmitterSubscriptions = consentCategories.map((category) =>
                sdk.listenForConsentChanges(category, sdkCallback)
            );
        }
        if (this.consentEmitterSubscriptions.length > 0 && this.watchers.length === 0) {
            this.consentEmitterSubscriptions.forEach((subscription) => subscription.remove());
            this.consentEmitterSubscriptions = [];
        }
    }

    watchConsentStatus(callback: ConsentWatcher): () => void {
        this.watchers.push(callback);
        this.setupSdkConsentListeners();
        return () => this.unwatchConsentStatus(callback);
    }

    unwatchConsentStatus(callback: ConsentWatcher): void {
        const watcherIndex = this.watchers.indexOf(callback);
        if (watcherIndex < 0) return;
        this.watchers.splice(watcherIndex, 1);
        this.setupSdkConsentListeners();
    }
}
